/**
 * Copyright (c) 2020 Visionet Systems, Inc
 */
import { getPriceForSelectedVariant, PriceForSelectedVariantInput } from '@msdyn365-commerce-modules/retail-actions';
import { IActionContext, IImageData, Image } from '@msdyn365-commerce/core';
import { AttributeValue, ProductPrice, IContext } from '@msdyn365-commerce/retail-proxy';
import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import * as React from 'react';
import { IModuleConfig, IVsiProductBadgesProps } from './vsi-product-badges.props';
import { vSIInitiateRestCallAsync } from '../../../../dotdigital-extension/PopularityBannerDataAction.g';
import { getDateObjFromString } from '../formatDate';

export enum badgePageType {
    plp = 'plp',
    pdp = 'pdp'
}

export interface IVsiProductBadgeState {
    // rating: number | undefined;
    productPrice: ProductPrice;
    productAttributes: AttributeValue[];
    badgeDisplay: boolean;
    imageBadge: boolean;
    showPromotionalBanner: boolean;
    showBannerStatus: boolean;
    totalProdTransaction: string;
}

/**
 *
 * VsiProductBadges Component is used to add badges on products
 * @extends {React.PureComponent<IVsiProductBadgesProps>}
 */

class VsiProductBadges extends React.PureComponent<IVsiProductBadgesProps, IVsiProductBadgeState> {
    constructor(props: IVsiProductBadgesProps) {
        super(props);
        this.state = {
            // TODO: Need to remove this
            //rating: 0,
            productPrice: {},
            productAttributes: [],
            badgeDisplay: false,
            imageBadge: false,
            showPromotionalBanner: true,
            showBannerStatus: false,
            totalProdTransaction: '0'
        };
    }

    private getBadgesFromAttributes = () => {
        const { moduleType, productComponentProduct } = this.props;

        let attributes;
        if (moduleType === badgePageType.plp && productComponentProduct) {
            attributes = productComponentProduct.AttributeValues;
        } else {
            attributes = this.state.productAttributes;
        }

        if (!attributes || attributes.length === 0) {
            return [];
        }

        const badgeAttr = attributes.find(attr => attr.Name?.toLowerCase() === 'badges');

        if (!badgeAttr?.TextValue) {
            return [];
        }

        try {
            return JSON.parse(badgeAttr.TextValue);
        } catch (e) {
            console.error('Invalid Badges attribute JSON', e);
            return [];
        }
    };

    private getCustomBadges = () => {
        if (!this.props.context.app.config.isCustomBadgeEnabled) {
            return null;
        }

        const now = new Date();
        const badges = this.getBadgesFromAttributes() || [];
        const activeBadges: any[] = [];

        if (this._shouldShowOutOfStock()) {
            activeBadges.push({
                BadgeType: 'Text',
                BadgeText: 'Out of stock',
                BadgeBgColor: '#F7F5EE',
                TextColor: '#C49934'
            });
        }

        badges.forEach((badge: { StartDate: string; EndDate: string }) => {
            try {
                const start = this.getDateFromDateString(badge.StartDate);
                const end = this.getDateFromDateString(badge.EndDate);
                if (now >= start && now <= end) {
                    activeBadges.push(badge);
                }
            } catch {}
        });

        return activeBadges.length ? activeBadges : null;
    };

    public findBannerData = async () => {
        if (!this.props.mediaGalleryProduct?.ItemId) return;
        try {
            const context: IContext = { callerContext: this.props.context.actionContext };
            const res = await vSIInitiateRestCallAsync(
                context,
                this.props.mediaGalleryProduct?.ItemId,
                this.props.context.app.config.popularityBannerHours,
                this.props.context.app.config.popularityBannerCompany
            );
            if (res.result) {
                this.setState({
                    showBannerStatus: res.result?.Status,
                    totalProdTransaction: res.result?.NoOfTransactions
                });
            } else {
                this.setState({
                    showBannerStatus: false,
                    totalProdTransaction: '0'
                });
            }
        } catch (err) {
            console.log(err);
        }
    };

    public componentDidUpdate = async (prevProps: IVsiProductBadgesProps) => {
        if (this.props !== prevProps) {
            await this.getDataOnUpdate();
        }
    };

    public componentDidMount = async () => {
        if (this.props.mediaGalleryProduct !== undefined || this.props.productComponentProduct !== undefined) {
            await this.getDataOnUpdate();
        }
        this.findBannerData();
    };

    public render(): JSX.Element | null {
        // badges config from app settings
        const appConfig = this.props.context.app.config;
        const moduleConfig: IModuleConfig = {
            badgeFontColor: appConfig.badgeFontColor,
            badgeBackgroundColor: appConfig.badgeBackgroundColor,
            badgeFontBold: appConfig.badgeFontBold,
            badgeShow: appConfig.badgeShow,
            badgeBorderShow: appConfig.badgeBorderShow,
            badgeRating: appConfig.badgeRating,
            imageBadgeLeftAlign: appConfig.imageBadgeLeftAlign
        };

        // return null to if badges are not enabled from config
        if (!moduleConfig.badgeShow || (this.props.mediaGalleryProduct === undefined && this.props.productComponentProduct === undefined)) {
            return null;
        }

        const color = moduleConfig.badgeFontColor || '#003428';
        const backgroundColor = moduleConfig.badgeBackgroundColor || '#F4EA7B';
        const fontWeight = moduleConfig.badgeFontBold ? 500 : 'normal';
        let border = moduleConfig.badgeBorderShow ? '1px solid #ababac' : 'none';
        if (this.props.moduleType === badgePageType.pdp) {
            border = 'none';
        }
        const rightfloat = moduleConfig.imageBadgeLeftAlign ? 'right' : 'none';

        const badgeStyle: React.CSSProperties = {
            color: color,
            backgroundColor: backgroundColor,
            border: border,
            fontWeight: fontWeight
        };

        //Exit Popularity Banner Badge
        const exitBadgeStyle: React.CSSProperties = {
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            alignContent: 'flex-end',
            marginRight: '10px'
        };

        const badgeHeadingStyle: React.CSSProperties = {
            display: 'flex',
            marginTop: '-10px',
            marginLeft: '5px',
            fontSize: '12px'
        };

        const badgeDescriptionStyle: React.CSSProperties = {
            display: 'flex',
            marginLeft: '5px',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            alignContent: 'flex-start',
            marginTop: '5px',
            fontSize: '12px',
            textAlign: 'left'
        };

        const badgeStyleRight: React.CSSProperties = {
            color: color,
            /* backgroundColor: backgroundColor, */
            float: rightfloat
        };

        let parentClassName = 'both';
        const showOutofStock = this._shouldShowOutOfStock();
        if (this.state.badgeDisplay && this.state.imageBadge) {
            parentClassName = 'both';
        } else if (this.state.badgeDisplay && !this.state.imageBadge) {
            parentClassName = 'left-badge';
        } else if (!this.state.badgeDisplay && this.state.imageBadge) {
            parentClassName = 'right-badge';
        }
        const isImageBadgeInSchedule = this.shouldImageBadgeBeRenderedAsPerSchedule();
        const isTextBadgeInSchedule = this.shouldTextBadgeBeRenderedAsPerSchedule();

        return (
            <div className={`badges ${parentClassName}`}>
                <div className='badges__top-style'>
                    {isTextBadgeInSchedule ? this._renderProductBadge(badgeStyle, showOutofStock) : <></>}
                </div>
                {this.state.showBannerStatus &&
                    Number(this.state.totalProdTransaction) >= this.props.context.app.config.popularityBannerThreshold &&
                    this._renderPromotionalBanner(
                        badgeStyle,
                        exitBadgeStyle,
                        badgeHeadingStyle,
                        badgeDescriptionStyle,
                        this.state.showPromotionalBanner,
                        this.state.totalProdTransaction
                    )}
                {isImageBadgeInSchedule ? this._renderImageBadge(badgeStyleRight, 'BadgeImage') : <></>}
            </div>
        );
    }
    private _shouldShowOutOfStock = () => {
        const { productAvailableData, moduleType, mediaGalleryProduct, productComponentProduct, isProductAvailableCNC } = this.props;
        //For PDP
        if (mediaGalleryProduct || productComponentProduct) {
            let recordId: number;
            let masterProductId: number | undefined;
            let productId: number;
            if (this.props.moduleType === badgePageType.pdp) {
                const product = mediaGalleryProduct;
                recordId = product!.RecordId;
                masterProductId = product!.MasterProductId;
                productId = masterProductId || recordId;

                let attributes;
                if (moduleType === badgePageType.plp && this.props.productComponentProduct) {
                    attributes = this.props.productComponentProduct.AttributeValues;
                } else {
                    attributes = this.state.productAttributes;
                }
                this.getProductAttrbute(this.props.context.actionContext, productId);
                const attName = attributes?.find(onlystore => onlystore.Name === 'OnlyInStore');
                const AttValue = attName?.TextValue?.toLocaleLowerCase();
                if (AttValue === 'no') {
                    if (productAvailableData) {
                        const { result } = productAvailableData;
                        if (result !== undefined && result.length > 0 && !result[0].IsProductAvailable && !isProductAvailableCNC) {
                            return true;
                        }
                    }
                }
            }
        }

        return false;
    };
    private _renderImageBadge(badgeStyle: React.CSSProperties, attributeName: string): JSX.Element | null {
        const { moduleType } = this.props;

        if (this.props.context.app.config.isCustomBadgeEnabled) {
            const activeBadges = this.getCustomBadges();
            if (activeBadges?.length) {
                const imageBadges = activeBadges.filter((b: { BadgeType: string }) => b.BadgeType === 'Image');

                if (imageBadges.length) {
                    this.setState({ imageBadge: true });

                    return (
                        <>
                            {imageBadges.map((b: { BadgeImage: any }, i: React.Key | undefined) => (
                                <div key={i} className={`${moduleType}-discount-badge badges__icon`} style={badgeStyle}>
                                    <Image
                                        src={`${this.props.context.request.apiSettings.baseImageUrl}Products/${b.BadgeImage}`}
                                        altText=''
                                        loadFailureBehavior='empty'
                                        imageSettings={this.props.imageSettings}
                                        gridSettings={this.props.context.request.gridSettings || {}}
                                    />
                                </div>
                            ))}
                        </>
                    );
                }
            }
        }

        let attributes;
        if (moduleType === badgePageType.plp && this.props.productComponentProduct) {
            attributes = this.props.productComponentProduct.AttributeValues;
        } else {
            attributes = this.state.productAttributes;
        }
        if (attributes && attributes.length > 0) {
            const attribute = attributes[attributes.findIndex((x: AttributeValue) => x.Name === attributeName)];
            if (attribute !== undefined) {
                const attributeValue = attribute.TextValue ? attribute.TextValue : undefined;
                const baseImageURL = `${this.props.context.request.apiSettings.baseImageUrl}Products/`;
                const badgeImage = attributeValue && attributeValue.trim().toLowerCase();

                if (!badgeImage || (attributeValue && attributeValue.trim().toLowerCase() === 'none')) {
                    return null;
                }
                const imageUrl = `${baseImageURL}${badgeImage}`;
                const img: IImageData = {
                    src: imageUrl,
                    altText: ''
                };

                const imageProps = {
                    gridSettings: this.props.context.request.gridSettings || {},
                    imageSettings: this.props.imageSettings
                };
                this.setState({ imageBadge: true });
                return (
                    <div className={`${moduleType}-discount-badge badges__icon`} style={badgeStyle}>
                        <Image {...img} {...imageProps} loadFailureBehavior='empty' />
                    </div>
                );
            }
        }
        return null;
    }

    private getDateFromDateString = (dateString: string): Date => {
        const dateTimeSplitted = dateString.split(' ');
        const startDateMonth = dateTimeSplitted[0].split('/')[1];
        const startDateDay = dateTimeSplitted[0].split('/')[0];
        const startDateYear = dateTimeSplitted[0].split('/')[2];
        const startDateHour = dateTimeSplitted[1].split(':')[0];
        const startDateMinutes = dateTimeSplitted[1].split(':')[1];
        const GMTStr = dateTimeSplitted[dateTimeSplitted.length - 1].includes('GMT') ? dateTimeSplitted[dateTimeSplitted.length - 1] : '';
        const badgeDate = getDateObjFromString(
            `${startDateMonth}-${startDateDay}-${startDateYear} ${startDateHour}:${startDateMinutes}:00 ${GMTStr}`,
            '-',
            true
        );
        // new Date(
        //     `${startDateMonth}-${startDateDay}-${startDateYear} ${startDateHour}:${startDateMinutes}:00 ${GMTStr}`
        // );
        return badgeDate;
    };

    private shouldImageBadgeBeRenderedAsPerSchedule = (): boolean => {
        if (this.props.context.app.config.isCustomBadgeEnabled) {
            const active = this.getCustomBadges()?.some((b: { BadgeType: string }) => b.BadgeType === 'Image');
            return !!active;
        }
        const moduleType = this.props.moduleType;
        let attributes;
        if (moduleType === badgePageType.plp && this.props.productComponentProduct) {
            attributes = this.props.productComponentProduct.AttributeValues;
        } else {
            attributes = this.state.productAttributes;
        }

        let startDateAttr = attributes?.find(attr => attr.Name?.toLowerCase() === 'badgestartdate1');
        let endDateAttr = attributes?.find(attr => attr.Name?.toLowerCase() === 'badgeenddate1');
        if (!startDateAttr || !endDateAttr) {
            return true;
        }
        if (!startDateAttr.TextValue) {
            return true;
        }
        if (!endDateAttr.TextValue) {
            return true;
        }
        try {
            const startDate = this.getDateFromDateString(startDateAttr.TextValue.trim()); //new Date(startDateAttr.TextValue.trim());
            const endDate = this.getDateFromDateString(endDateAttr.TextValue.trim()); //new Date(endDateAttr.TextValue.trim());
            if (!startDate || !endDate) {
                return true;
            }
            const now = new Date();
            if (now.getTime() <= endDate.getTime() && now.getTime() >= startDate.getTime()) {
                return true;
            }
        } catch (e) {
            return true;
        }
        return false;
    };

    private shouldTextBadgeBeRenderedAsPerSchedule = (): boolean => {
        if (this.props.context.app.config.isCustomBadgeEnabled) {
            const active = this.getCustomBadges()?.some((b: { BadgeType: string }) => b.BadgeType === 'Text');
            return !!active;
        }
        if (this.props.isUnbxdWidget && (this.props.isOnlyStore || this.props.isOutOfStock)) {
            return true;
        }
        const moduleType = this.props.moduleType;
        let attributes;
        if (moduleType === badgePageType.plp && this.props.productComponentProduct) {
            attributes = this.props.productComponentProduct.AttributeValues;
        } else {
            attributes = this.state.productAttributes;
        }

        let startDateAttr = attributes?.find(attr => attr.Name?.toLowerCase() === 'textbadgestartdate');
        let endDateAttr = attributes?.find(attr => attr.Name?.toLowerCase() === 'textbadgeenddate');
        if (!startDateAttr || !endDateAttr) {
            return true;
        }
        if (!startDateAttr.TextValue) {
            return true;
        }
        if (!endDateAttr.TextValue) {
            return true;
        }
        try {
            const startDate = this.getDateFromDateString(startDateAttr.TextValue.trim()); //new Date(startDateAttr.TextValue.trim());
            const endDate = this.getDateFromDateString(endDateAttr.TextValue.trim()); //new Date(endDateAttr.TextValue.trim());
            if (!startDate || !endDate) {
                return true;
            }
            const now = new Date();
            if (now.getTime() <= endDate.getTime() && now.getTime() >= startDate.getTime()) {
                return true;
            }
        } catch (e) {
            return true;
        }
        return false;
    };

    private _renderDiscountBadgePercentage(badgeStyle: React.CSSProperties): JSX.Element | null {
        const { moduleType } = this.props;
        const price = this.state.productPrice;
        if (price) {
            const { BasePrice, CustomerContextualPrice } = price;
            let discount = 0;
            if (BasePrice && CustomerContextualPrice && BasePrice > CustomerContextualPrice) {
                const actualPercentage = Math.round((CustomerContextualPrice / BasePrice) * 100);
                discount = 100 - actualPercentage;
            }

            if (discount > 0) {
                this.setState({ badgeDisplay: true });
                return (
                    <div className={`${moduleType}-discount-badge badges__top`} style={badgeStyle}>
                        {`-${discount}%`}
                    </div>
                );
            }
        }
        return null;
    }

    // TODO: Need to remove this
    // private _renderRatingBadge(badgeStyle: React.CSSProperties): JSX.Element | null {
    //     const { moduleType, context } = this.props;
    //     const rating = this.state.rating;
    //     const topRating = context.app.config.badgeRating;
    //     if (rating && rating >= topRating) {
    //         this.setState({ badgeDisplay: true });
    //         let badgeRating = context.app.config.badgeRatingText;
    //         if (
    //             this.props.context.app.config.textBadgesCharacterLimit &&
    //             badgeRating &&
    //             badgeRating.length > this.props.context.app.config.textBadgesCharacterLimit
    //         ) {
    //             // badgeRating =  `${badgeRating.substring(0, this.props.context.app.config.textBadgesCharacterLimit)}`;
    //             return <></>;
    //         }
    //         return (
    //             <div className={`${moduleType}-rating badges__top`} style={badgeStyle}>
    //                 {`${badgeRating}`}
    //             </div>
    //         );
    //     }
    //     return null;
    // }

    private _renderOutofStockBadge(badgeStyle: React.CSSProperties): JSX.Element | null {
        const { moduleType } = this.props;
        this.setState({ badgeDisplay: true });
        return (
            <div className={`${moduleType}-discount-badge badges__oos`} style={badgeStyle}>
                {`Out of stock`}
            </div>
        );
    }

    //Render Promotional Badge
    private _renderPromotionalBanner(
        badgeStyle: React.CSSProperties,
        exitBadgeStyle: React.CSSProperties,
        badgeHeadingStyle: React.CSSProperties,
        badgeDescriptionStyle: React.CSSProperties,
        showPromotionalBanner: boolean,
        totalProdTransaction: string
    ): JSX.Element | null {
        const { moduleType } = this.props;
        const _displayPromotionalBanner = () => {
            this.setState({
                showPromotionalBanner: false
            });
        };

        if (moduleType === badgePageType.pdp && showPromotionalBanner) {
            return (
                <div className={`${moduleType}-discount-badge promotionalBanner__top`}>
                    <p className='exit-badge-style' onClick={_displayPromotionalBanner}></p>
                    <p className='badge-heading'>Trending</p>
                    <p className='badge-description'>
                        {totalProdTransaction} people bought this within the past {this.props.context.app.config.popularityBannerHours}{' '}
                        hours
                    </p>
                </div>
            );
        }
        return null;
    }

    // Render discountAmount badge
    private _renderDiscountBadgeAmount(badgeStyle: React.CSSProperties): JSX.Element | null {
        const { moduleType } = this.props;
        const price = this.state.productPrice;
        if (price) {
            const { BasePrice, CustomerContextualPrice } = price;
            let discount = 0;
            if (BasePrice && CustomerContextualPrice && BasePrice > CustomerContextualPrice) {
                discount = Number((BasePrice - CustomerContextualPrice).toFixed(2));
            }
            if (discount > 0) {
                // format price
                const discountWithCurrenceySign =
                    discount &&
                    this.props.context.cultureFormatter.formatCurrency(discount.toString(), this.props.context.request.channel?.Currency);
                this.setState({ badgeDisplay: true });
                return (
                    <div className={`${moduleType}-discount-badge badges__top`} style={badgeStyle}>
                        {`${discountWithCurrenceySign} OFF`}
                    </div>
                );
            }
        }
        return null;
    }

    // Render custom badge
    private _renderCustomAttributeBadge(badgeStyle: React.CSSProperties, attributeName: string): JSX.Element | null {
        const { moduleType } = this.props;
        let attributes;
        if (moduleType === badgePageType.plp && this.props.productComponentProduct) {
            attributes = this.props.productComponentProduct.AttributeValues;
        } else {
            attributes = this.state.productAttributes;
        }
        if (attributes && attributes.length > 0) {
            const attribute = attributes[attributes.findIndex((x: AttributeValue) => x.Name === attributeName)];
            if (attribute !== undefined) {
                // const brandValue = attrbute && attrbute.TextValue ? attrbute.TextValue : 'SizeNotDefined';
                let attributeValue = attribute.TextValue ? attribute.TextValue : '';
                const attributeValues = attributeValue.split(',');
                const allAttributename = attributeValues.map(attributeValue => {
                    const badgesThemesColors = this.props.context.app.config.badgesThemeColors;
                    let badgeThemeColor = '';
                    let badgeTextColor = '';
                    if (Array.isArray(badgesThemesColors)) {
                        for (const badgeDetail of badgesThemesColors) {
                            // @ts-ignore
                            if (badgeDetail['badgeName'] && (badgeDetail['badgeName'] as string) === attributeValue) {
                                // @ts-ignore
                                (badgeThemeColor = badgeDetail['badgeColor']), (badgeTextColor = badgeDetail['badgeTextColor']);
                            }

                            if (
                                (badgeDetail['badgeName'] && (badgeDetail['badgeName'] as string) == 'Sale') ||
                                (badgeDetail['badgeName'] && (badgeDetail['badgeName'] as string) == 'sale')
                            ) {
                                badgeTextColor = '#fff';
                            }
                        }
                    }
                    if (attributeValue.length > 0) {
                        this.setState({ badgeDisplay: true });
                        if (
                            this.props.context.app.config.textBadgesCharacterLimit &&
                            attributeValue &&
                            attributeValue.length > this.props.context.app.config.textBadgesCharacterLimit
                        ) {
                            attributeValue = `${attributeValue.substring(
                                0,
                                this.props.context.app.config.textBadgesCharacterLimit - 3
                            )}...`;
                        }
                        const badgeBg = { background: `${badgeThemeColor}`, color: `${badgeTextColor}` };
                        return (
                            <div className={`${moduleType}-discount-badge badges__top`} style={badgeBg}>
                                {attributeValue}
                            </div>
                        );
                    }
                    return null;
                });
                return <>{allAttributename}</>;
            }
        }
        return null;
    }

    private renderDefaultBadge = (badgeStyle: React.CSSProperties) => {
        const { moduleType, productComponentProduct } = this.props;
        let badgeText = productComponentProduct?.discount;
        if (
            this.props.context.app.config.textBadgesCharacterLimit &&
            badgeText &&
            badgeText.length > this.props.context.app.config.textBadgesCharacterLimit
        ) {
            //badgeText =  `${badgeText.substring(0, this.props.context.app.config.textBadgesCharacterLimit)}`;
            return <></>;
        }
        return <div className={`${moduleType}-discount-badge badges__top`}>{badgeText}</div>;
    };

    private _renderProductBadge(badgeStyle: React.CSSProperties, showOutofStock: boolean): JSX.Element | null {
        const { moduleType, context } = this.props;
        const badgeShow = context.app.config.badgeShow;
        if (this.props.context.app.config.isCustomBadgeEnabled) {
            const activeBadges = this.getCustomBadges();

            if (activeBadges?.length) {
                const textBadges = activeBadges.filter((b: { BadgeType: string }) => b.BadgeType === 'Text');

                if (textBadges.length) {
                    this.setState({ badgeDisplay: true });

                    return (
                        <>
                            {textBadges.map((b, i) => (
                                <div
                                    key={i}
                                    className={`${moduleType}-discount-badge badges__top`}
                                    style={{
                                        background: b.BadgeBgColor || badgeStyle.backgroundColor,
                                        color: b.TextColor || badgeStyle.color,
                                        border: badgeStyle.border,
                                        fontWeight: badgeStyle.fontWeight
                                    }}
                                >
                                    {b.BadgeText}
                                </div>
                            ))}
                        </>
                    );
                }
            }
        }

        let attributes;
        if (moduleType === badgePageType.plp && this.props.productComponentProduct) {
            attributes = this.props.productComponentProduct.AttributeValues;
        } else {
            attributes = this.state.productAttributes;
        }

        let isComingSoon = false;

        const comingSoonCategoryName: string | undefined = this.props.context.app.config.comingSoonCategoryName;
        if (comingSoonCategoryName && attributes && attributes.length > 0) {
            const comingSoonAttr = attributes.find(attr => attr.Name?.toLowerCase() === comingSoonCategoryName?.toLowerCase());
            if (comingSoonAttr && comingSoonAttr.TextValue?.toLowerCase() === 'yes') {
                isComingSoon = true;
            }
        }
        if (this.props.isUnbxdWidget && this.props.isOutOfStock && !this.props.isFetchingInventory) {
            return <div className={`${moduleType}-discount-badge badges__top`}>Out of stock</div>;
        }

        if (this.props.isUnbxdWidget && this.props.isOnlyStore) {
            return <div className={`${moduleType}-discount-badge badges__top`}>Click & collect only</div>;
        }

        if (!isComingSoon && showOutofStock && badgeShow) {
            return this._renderOutofStockBadge(badgeStyle);
        }
        if (this.props.productComponentProduct?.discount) {
            return this.renderDefaultBadge(badgeStyle);
        }
        let attributeValue;
        if (attributes && attributes.length > 0) {
            const attribute =
                attributes[
                    attributes.findIndex((x: AttributeValue) => {
                        const name = x.Name && x.Name.toLowerCase();
                        return name === 'badgetype';
                    })
                ];
            if (attribute !== undefined) {
                attributeValue = attribute.TextValue ? attribute.TextValue : undefined;
                const BadgeType = attributeValue;

                if (badgeShow === true && BadgeType !== undefined) {
                    if (BadgeType === 'DiscountAmount') {
                        return this._renderDiscountBadgeAmount(badgeStyle);
                    } else if (BadgeType === 'DiscountPercentage') {
                        return this._renderDiscountBadgePercentage(badgeStyle);
                    }
                    // TODO: Need to remove this
                    // else if (BadgeType === 'Rating') {
                    //     // return this._renderRatingBadge(badgeStyle);
                    // }
                    else if (BadgeType === 'BadgeText' && BadgeType !== undefined) {
                        return this._renderCustomAttributeBadge(badgeStyle, BadgeType);
                    } else {
                        return null;
                    }
                } else {
                    return null;
                }
            }
        }

        return null;
    }

    private getDataOnUpdate = async () => {
        const { mediaGalleryProduct, productComponentProduct, context } = this.props;

        if (mediaGalleryProduct || productComponentProduct) {
            let recordId: number;
            let masterProductId: number | undefined;
            let productId: number;
            // if request is from Buybox/PDP
            if (this.props.moduleType === badgePageType.pdp) {
                const product = mediaGalleryProduct;
                recordId = product!.RecordId;
                masterProductId = product!.MasterProductId;
                productId = masterProductId || recordId;
                // TODO: Need to remove this
                //     const ratingInput = new GetRatingsSummaryInput(
                //     productId.toString(),
                //     getRnrPartnerId(context.actionContext),
                //     getRnrEndpointId(context.actionContext)
                // );
                // const ratings = await getRatingsSummary(ratingInput, context.actionContext);
                // const ratingValue = ratings ? ratings.averageRating : 0;
                // this.setState({ rating: ratingValue });
                // Get product pricing
                const priceInput = new PriceForSelectedVariantInput(
                    productId,
                    context.request.apiSettings.channelId,
                    product,
                    context.actionContext.requestContext.user.customerAccountNumber
                );
                // Run and await the result of the data action
                const productPriceResult = await getPriceForSelectedVariant(priceInput, context.actionContext);
                const price: ProductPrice = {
                    // BasePrice value has been set based on higher price value among these ↓.
                    BasePrice: Math.max(
                        productPriceResult?.BasePrice || 0,
                        productPriceResult?.TradeAgreementPrice || 0,
                        productPriceResult?.AdjustedPrice || 0
                    ),
                    AdjustedPrice: product!.Price || 0,
                    CustomerContextualPrice: productPriceResult!.CustomerContextualPrice || 0
                };
                this.setState({ productPrice: price });
                // tslint:disable-next-line: no-floating-promises
                this.getProductAttrbute(context.actionContext, productId);
            } else if (this.props.moduleType === badgePageType.plp) {
                const product = productComponentProduct;
                const price: ProductPrice = {
                    BasePrice: (product && product.BasePrice) || 0,
                    AdjustedPrice: (product && product.Price) || 0,
                    CustomerContextualPrice: (product && product.Price) || 0
                };
                // TODO: Need to remove this
                // this.setState({ rating: productComponentProduct ? productComponentProduct.AverageRating : 0 });
                this.setState({ productPrice: price });
                // tslint:disable-next-line: no-floating-promises
                // this.getProductAttrbute(context.actionContext, product!.RecordId);
            }
        }
    };

    private getProductAttrbute = async (callerContext: IActionContext, productId: number) => {
        const productAttributeResult = await getAttributeValuesAsync(
            { callerContext, queryResultSettings: {} },
            productId,
            callerContext.requestContext.apiSettings.channelId,
            callerContext.requestContext.apiSettings.catalogId
        );
        this.setState({ productAttributes: productAttributeResult });
    };
}

export default VsiProductBadges;
